<template>
    <div class="row mx-0">
        <div class="p-0">
            <!-- Dashboard (same conditions as BenchmarkViewWiland: when show_dashboard is true) -->
            <div v-if="show_dashboard" class="col-12">
                <benchmark-view-wiland-dashboard
                    :user="user"
                    :client="client"
                    :connections="connections"
                    :section_id="null"
                    :dashboard_pages="dashboard_pages"
                    :report_groups="report_groups"
                    @goToSection="$emit('goToSection', $event)"
                    @goToViewAll="$emit('goToViewAll')"
                />
            </div>
            <div class="row mx-0" v-if="page != null && !show_dashboard">
                <div class="p-0">
                    <div v-for="(section, sectionIdx) in sections"  :key="section.id" :class="standalone ? '' : (section.is_section_tab_active ? '' : 'd-none')">
                        <div class="row mb-3 mt-3" v-if="if_loading && (!standalone || if_show_top_charts)">
                            <div class="col-12">
                                <div class="navbar navbar-expand bg-secondary rounded p-3">
                                    <h4 class="mb-0 text-primary"><span class="d-inline-block spinner-border spinner-border-sm me-2" role="status"></span>
                                    Your benchmark report is loading...</h4>
                                </div>
                            </div>
                        </div>
                        <!-- Charts (single block; no tab bar) -->
                        <div v-if="!if_loading && (standalone || sectionIdx!=0) && section.charts && section.charts.length > 0" class="report-section">
                            <div class="chart-grid">
                                <div class="row">
                                    <template v-for="(chart, i) in section.charts" :key="chart.id || i">
                                        <div
                                            v-if="checkIfShowChart(chart?.chart) && (chart?.chart?.type !== 'multi_metric_scorecard' || !standalone || if_show_top_charts)"
                                            :class="getChartSizeClass(chart)"
                                            :id="chart.id"
                                            class="size-container d-flex flex-column"
                                            :data-size="chart.size"
                                        >
                                            <div class="d-flex flex-column flex-grow-1" v-if="!chart?.title || !chart.title.toLowerCase().startsWith('divider')">
                                                <div class="chart-container card position-relative mb-3 flex-grow-1" :class="'benchmark-'+chart?.subtitle">
                                                    <Transition name="fade" :duration="300">
                                                        <div v-if="if_refreshing && chart.id != applied_chart_filter?.section_chart_id && chart?.chart?.type!='information_card'" class="loading-overlay">
                                                            <div role="status" class="d-inline-block spinner-border spinner-border-lg ms-2"><span class="visually-hidden">Loading</span></div>
                                                            <p>Loading...</p>
                                                        </div>
                                                    </Transition>
                                                    <div class="grow">
                                                        <chart-template
                                                            :chart="chart.chart"
                                                            :section_title="chart.title"
                                                            :id="chart.id"
                                                            :key="chart.id || i"
                                                            :if_refreshing="if_refreshing"
                                                            :if_on_report_page="true"
                                                            :applied_chart_filter="applied_chart_filter"
                                                            :if_benchmark_report="true"
                                                            :show_data_labels="chart.show_data_labels !== undefined ? chart.show_data_labels : page_show_data_labels"
                                                            :chart_show_data_labels="chart.show_data_labels"
                                                            :page_show_data_labels="page_show_data_labels"
                                                            :allow_download="client != null && chart?.chart?.type!='scorecard' && chart?.chart?.type!='information_card' && canExport('benchmarks')"
                                                            :allow_label_toggle="chart?.chart?.type !== 'scorecard' && chart?.chart?.type !== 'information_card' && chart?.chart?.type !== 'table' && chart?.chart?.type !== 'multi_metric_scorecard'"
                                                            :allow_resize="chart?.chart?.type !== 'scorecard' && chart?.chart?.type !== 'information_card' && chart?.chart?.type !== 'multi_metric_scorecard'"
                                                            :allow_deep_dive="true"
                                                            :current_size="chart.size"
                                                            :page_filters="local_page?.filters || []"
                                                            :page_dimensions="local_page?.dimensions || []"
                                                            :section_filters="section?.filters || []"
                                                            :section_id="section?.id"
                                                            :is_filter_panel_expanded="is_filter_panel_expanded"
                                                            :is_section_active="standalone || section.is_section_tab_active"
                                                            :should_show_summary="shouldShowSummary"
                                                            :chart_summary="chart_summaries[chart.id]"
                                                            @chartReferenceClick="onChartReferenceClick"
                                                            @resizeChart="changeChartSize($event, chart.id, section)"
                                                            @toggleDataLabels="toggleDataLabels(chart.id, $event, section)"
                                                            @downloadChart="downloadChart(chart, section)"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    
                                    <!-- Previous/Next section links (hidden in standalone mode); order follows report group -->
                                    <div v-if="!standalone && (sectionIdx === 0 || previousSectionInOrder || nextSectionInOrder)" class="col-12">
                                        <div v-if="sectionIdx === 1" class="float-start">
                                            <h4><button type="button" class="btn btn-link p-0 text-decoration-none" @click="$emit('goToDashboard')"><i class="fa fas fa-arrow-left"></i> Previous: Benchmark Library</button></h4>
                                        </div>
                                        <div v-else-if="sectionIdx > 1 && previousSectionInOrder" class="float-start">
                                            <h4><button type="button" class="btn btn-link p-0 text-decoration-none" @click="goToPreviousSection"><i class="fa fas fa-arrow-left"></i> Previous: {{ getPreviousSectionTitle(sectionIdx) }}</button></h4>
                                        </div>
                                        <div v-if="nextSectionInOrder" class="float-end">
                                            <h4><button type="button" class="btn btn-link p-0 text-decoration-none" @click="goToNextSection">Next Page: {{ getNextSectionTitle(sectionIdx) }} <i class="fa fas fa-arrow-right"></i></button></h4>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Swal from 'sweetalert2';
    import { defineComponent, ref, nextTick } from 'vue';
    import { Tooltip } from 'bootstrap';
    import { Context } from 'svgcanvas';
    import ChartDataLabels from 'chartjs-plugin-datalabels';
    import permissionsMixin from '../../mixins/permissions';
    import chartExportMixin from '../../mixins/chart_export';
    import { useNotifications } from '../../composables/useNotifications';
    import { useUserStore } from '../../stores/user';

    import BenchmarkViewWilandDashboard from './BenchmarkViewWilandDashboard.vue';
    import BenchmarkViewWilandDemoCta from './BenchmarkViewWilandDemoCta.vue';

    export default defineComponent({
        components: {
            Swal,
            BenchmarkViewWilandDashboard,
            BenchmarkViewWilandDemoCta
        },
        mixins: [permissionsMixin, chartExportMixin],
        props: {
            user: {
                type: [Object, null],
            },
            client: {
                type: [Object, null],
            },
            connections: {
                type: [Array, null],
            },
            section_id: {
                type: [Number, null],
            },
            page_show_data_labels: {
                type: Boolean,
                default: false
            },
            if_refreshing: {
                type: Boolean,
                default: false
            },
            standalone: {
                type: Boolean,
                default: false
            },
            if_show_top_charts: {
                type: Boolean,
                default: false
            },
            initial_page: {
                type: [Object, null],
                default: null
            },
            report_groups: {
                type: Array,
                default: () => []
            },
            show_dashboard: {
                type: Boolean,
                default: false
            },
            dashboard_pages: {
                type: Array,
                default: () => []
            }
        },
        setup() {
            const benchmarkPage = ref(null);
            const reportSections = ref([]);
            const tooltips = ref([]);
            const { showNotification } = useNotifications();
            const userStore = useUserStore();

            return {
                benchmarkPage,
                reportSections,
                tooltips,
                showNotification,
                userStore
            };
        },
        data() {
            return {
                page: null,
                if_loading: true,
                local_page: {},
                benchmark_page: null,
                wiland_page: null,
                meta_data: {
                    revenue: '',
                    org: '',
                    donors: ''
                },
                active_tab_index: 1,
                active_view_tab: 'overview',
                is_filter_panel_expanded: false,
                applied_chart_filter: {},
                params: "",
                selected_ticket_type: '',
                if_mobile: window.innerWidth < 768,
                sidebar_collapsed: localStorage.getItem('sidebar_collapsed') === 'true',
                local_charts: {},
                chart_summaries: {},
                echoChannel: null,
                demoForm: {
                    organization_type: '',
                    annual_donors: '',
                    discussion_topic: '',
                    errors: {},
                    submitting: false
                }
            };
        },
        beforeMount() {
            if (this.initial_page && (this.standalone || Object.keys(this.initial_page).length > 0)) {
                this.syncPageFromInitial(this.initial_page);
                return;
            }
            this.loadBenchmark();
        },
        mounted() {
            this.onResize = () => {
                this.if_mobile = window.innerWidth < 768;
            };
            window.addEventListener('resize', this.onResize);
            this.onResize();
            window.addEventListener('storage', this.handleSidebarStorageChange);
            this.sidebar_collapsed = localStorage.getItem('sidebar_collapsed') === 'true';
            window.addEventListener('sidebar-toggle', this.handleSidebarToggleEvent);
            
            nextTick(() => {
                this.initializeTooltips();
            });

            this.setupChartSummaryListener();
        },
        computed: {
            shouldShowSummary() {
                return this.user?.preferences?.show_ai_summaries !== false;
            },
            date_type() {
                if(this.page?.data_blend_type == 'constituent')
                    return "Transaction ";
                else if(this.page?.data_blend_type == 'subscriber')
                    return "Email ";
                return "";
            },
            sections() {
                if (this.standalone && this.section_id) {
                    const allSections = this.local_page.sections || [];
                    const matchingSection = allSections.find(s => s.id == this.section_id);
                    return matchingSection ? [matchingSection] : [];
                }
                return this.local_page.sections || [];
            },
            orderedSections() {
                const raw = this.local_page.sections || [];
                if (!raw.length) return raw;
                const [placeholder, ...rest] = raw;
                const groups = this.report_groups || [];
                const orderByGroup = (a, b) => {
                    const idxA = groups.findIndex(g => g.group_name && (a.title || a.group_name || '').toLowerCase().includes(g.group_name.toLowerCase().trim()));
                    const idxB = groups.findIndex(g => g.group_name && (b.title || b.group_name || '').toLowerCase().includes(g.group_name.toLowerCase().trim()));
                    const orderA = idxA === -1 ? 999 : (groups[idxA]?.order ?? idxA);
                    const orderB = idxB === -1 ? 999 : (groups[idxB]?.order ?? idxB);
                    return orderA - orderB;
                };
                return [placeholder, ...[...rest].sort(orderByGroup)];
            },
            nextSectionInOrder() {
                const raw = this.local_page.sections || [];
                if (raw.length < 2) return null;
                const ordered = this.orderedSections;
                const currentSection = raw.find((_, i) => i === this.active_tab_index);
                if (!currentSection) return null;
                const pos = ordered.findIndex(s => s.id === currentSection.id);
                if (pos < 0 || pos >= ordered.length - 1) return null;
                return ordered[pos + 1];
            },
            previousSectionInOrder() {
                const raw = this.local_page.sections || [];
                if (raw.length < 2) return null;
                const ordered = this.orderedSections;
                const currentSection = raw.find((_, i) => i === this.active_tab_index);
                if (!currentSection) return null;
                const pos = ordered.findIndex(s => s.id === currentSection.id);
                if (pos <= 1) return null;
                return ordered[pos - 1];
            },
            applied_filters() {
                if (!this.local_page || !Array.isArray(this.local_page.dimensions)) {
                    return [];
                }

                let applied_filters_list = [];

                for (var i = 0; i < this.local_page.dimensions.length; i++) {
                    let dim = this.local_page.dimensions[i];

                    const checkFilled = (val, fieldType) => {
                        if (!val) return false;
                        if (Array.isArray(val)) return val.length > 0;
                        if (typeof val === 'object') {
                            if (fieldType === 'date_range' || fieldType === 'month_range') {
                                return val.start_date || val.end_date;
                            }
                            return Object.keys(val).length > 0;
                        }
                        return val !== '' && val !== null;
                    };

                    const hasValue = checkFilled(dim.default_value, dim.field_type) || checkFilled(dim.value, dim.field_type);

                    if (!hasValue) continue;

                    if ((dim.field_type === 'multi_select' || dim.field_type === 'month_picker' || dim.field_type === 'array_single_select' || dim.field_type === 'array_multi_select') && Array.isArray(dim.value) && dim.options && dim.value.length === dim.options.length) {
                        continue;
                    }

                    // Vertical and Annual Revenue Range: only show in applied row when a subset is selected (not "all options")
                    if (['org_vertical', 'org_annual_revenue'].includes(dim.column)) {
                        if (!dim.options || !Array.isArray(dim.value) || dim.value.length >= dim.options.length) {
                            continue;
                        }
                    }

                    //hide Time Period filter from badges and filter count
                    if (dim.field_type === 'single_select') {
                        const singleSelectOptionCount = (dim.options && dim.options.length) ? dim.options.length : (dim.column === 'period_type' ? 2 : 0);
                        if (singleSelectOptionCount === 2) {
                            continue;
                        }
                    }

                    const isDonation = (val) => Array.isArray(val) ? (val.length === 1 && val[0] === 'Donation') : false;
                    if (!isDonation(dim.default_value) && !isDonation(dim.value)) {
                        applied_filters_list.push({
                            filter_id: dim.id,
                            filter_label: dim.label,
                            filter_value: (dim.value != null ? dim.value : dim.default_value),
                            filter_default_value: ((typeof dim.column === 'string' || dim.column instanceof String) && dim.column.indexOf('time_period.year_type') !== -1 ? this.client?.default_reporting_period : dim.default_value),
                            filter_column_name: dim.column,
                            filter_type: dim.field_type,
                            is_required: dim.is_required
                        })
                    }
                }
                return applied_filters_list;
            },
            filter_count() {
                return this.applied_filters.length;
            },
            mainContentClass() {
                return {
                    'has-filter-panel-open': this.is_filter_panel_expanded,
                    'has-filter-panel-closed': !this.is_filter_panel_expanded,
                    'sidebar-collapsed': this.sidebar_collapsed,
                    'sidebar-expanded': !this.sidebar_collapsed,
                };
            },
        },
        watch: {
            initial_page: {
                handler(newPage) {
                    if (!newPage || Object.keys(newPage).length === 0) {
                        return;
                    }
                    this.syncPageFromInitial(newPage);
                },
                deep: true
            },
            section_id: {
                handler(newVal) {
                    if (this.section_id > 0 && this.local_page && this.local_page.sections) {
                        var index = this.local_page.sections.findIndex(section => section.id == this.section_id);
                        if (index >= 0) {
                            this.setActiveTab(index);
                        } else {
                            this.setActiveTab(0);
                        }
                    }
                }
            },
            page: {
                handler(newValue) {
                    if (newValue) {
                        nextTick(() => {
                            this.initializeTooltips();
                        });
                    }
                }
            }
        },
        methods: {
            syncPageFromInitial(initialPage) {
                this.page = JSON.parse(JSON.stringify(initialPage));
                this.local_page = JSON.parse(JSON.stringify(initialPage));
                if (this.standalone) {
                    this.benchmark_page = this.page;
                    this.wiland_page = this.page;
                }
                const idx = (this.local_page.sections || []).findIndex(s => s.id == this.section_id);
                if (idx >= 0) {
                    this.setActiveTab(idx);
                } else {
                    this.loadSectionTab();
                }

                const hasChartData = (this.local_page.sections || []).some(s =>
                    s.charts && s.charts.length > 0
                );

                if (hasChartData) {
                    this.initializeLocalCharts();
                } else {
                    this.if_loading = true;
                }
            },
            loadBenchmark() {
                var self = this;
                let benchmarkUrl;
                let wilandBenchmarkUrl;
                
                if(this.client == null) {
                    benchmarkUrl = '/api/benchmarks/demo/10294/charts';
                    wilandBenchmarkUrl = '/api/wiland_benchmarks/demo/10123/charts';
                } else {
                    benchmarkUrl = '/api/benchmarks/' + this.client.url + "/10294/charts";
                    wilandBenchmarkUrl = '/api/wiland_benchmarks/' + this.client.url + "/10123/charts";
                }
                
                if (this.section_id > 0) {
                    benchmarkUrl += '/' + this.section_id;
                    wilandBenchmarkUrl += '/' + this.section_id;
                }

                self.if_loading = true;

                Promise.all([
                    window.axios.get(benchmarkUrl),
                    window.axios.get(wilandBenchmarkUrl)
                ])
                    .then(([benchmarkResponse, wilandResponse]) => {
                        const benchmarkPage = benchmarkResponse.data.page;
                        const wilandPage = wilandResponse.data.page;
                        const benchmarkChartSummaries = benchmarkResponse.data.chart_summaries || {};
                        const wilandChartSummaries = wilandResponse.data.chart_summaries || {};
                        this.chart_summaries = {
                            ...benchmarkChartSummaries,
                            ...wilandChartSummaries
                        };
                        
                        self.benchmark_page = JSON.parse(JSON.stringify(benchmarkPage));
                        self.wiland_page = JSON.parse(JSON.stringify(wilandPage));
                        
                        self.page = JSON.parse(JSON.stringify(wilandPage));
                        self.local_page = JSON.parse(JSON.stringify(wilandPage));
                        
                        const combinedSections = [];
                        
                        combinedSections.push({
                            id: 0,
                            title: '',
                            is_section_tab_active: false
                        });
                        
                        if (wilandPage.sections && wilandPage.sections.length > 0) {
                            combinedSections.push({
                                ...wilandPage.sections[0],
                                is_section_tab_active: false
                            });
                        }
                        
                        if (benchmarkPage.sections && benchmarkPage.sections.length > 0) {
                            combinedSections.push({
                                ...benchmarkPage.sections[0],
                                is_section_tab_active: false
                            });
                        }
                        
                        self.local_page.sections = combinedSections;
                        self.page.sections = combinedSections;
                        
                        if (benchmarkPage.dimensions && benchmarkPage.dimensions.length > 0) {
                            self.local_page.dimensions = benchmarkPage.dimensions;
                            self.page.dimensions = benchmarkPage.dimensions;
                        }
                        
                        self.setDefaultFilterValues();
                        self.loadSectionTab();
                        self.initializeLocalCharts();
                        
                        if (self.section_id > 0) {
                            var index = self.local_page.sections.findIndex(section => section.id == self.section_id);
                            if (index >= 0) {
                                self.setActiveTab(index);
                            } else {
                                self.setActiveTab(1);
                            }
                        }
                    })
                    .catch(error => {
                        console.error('[BenchmarkViewWilandSection] Error loading benchmark:', error);
                        self.if_loading = false;
                    });
            },
            initializeLocalCharts() {
                if (!this.local_page.sections) return;
                this.chart_summaries = {};
                
                this.local_page.sections.forEach((section) => {
                    if (section.charts) {
                        section.charts.forEach((chart) => {
                            chart.show_data_labels = undefined;
                            chart.chart.size = chart.size;
                        });
                    }
                });
                this.if_loading = false;
            },
            checkIfShowChart(chart) {
                if (!chart) return false;
                if (chart.type == 'information_card' && chart.html != '') return true;
                if (this.page?.cache_only == true) return true;
                if (!chart.data || Object.keys(chart.data).length == 0) return false;
                if (!chart.labels || chart.labels.length == 0 || (chart.labels.length == 1 && chart.labels[0] && chart.labels[0] == '(No Data)')) return false;
                return true;
            },
            getChartSizeClass(chart) {
                if (this.is_filter_panel_expanded) {
                    return 'col-12';
                }
                
                if (chart.size == 'small') return 'col-md-4 col-12';
                if (chart.size == 'medium') return 'col-md-6 col-12';
                if (chart.size == 'large') return 'col-md-8 col-12';
                if (chart.size == 'fullsize' || chart.size == 'x-large') return 'col-12';
                return 'col-12';
            },
            changeChartSize(event, chartId, section) {
                const { size } = event;
                const charts = section.charts;
                if (charts) {
                    const chartIndex = charts.findIndex(c => c.id === chartId);
                    if (chartIndex >= 0) {
                        charts[chartIndex].size = size;
                        if (charts[chartIndex].chart) {
                            charts[chartIndex].chart.size = size;
                        }
                        this.$forceUpdate();
                        if (size === 'x-large') {
                            this.fetchChartSummaryForResize(charts[chartIndex], section);
                        }
                    }
                }
            },
            onChartReferenceClick({ chartId, sectionId, isSameSection }) {
                this.scrollToReferencedChart(chartId, sectionId, isSameSection);
            },
            scrollToReferencedChart(chartId, sectionId, isSameSection) {
                const chartIdToFind = chartId != null ? String(chartId) : null;
                if (!chartIdToFind) {
                    return;
                }

                const scrollAndHighlight = () => {
                    const chartElement = document.getElementById(chartIdToFind);
                    if (!chartElement) {
                        return;
                    }

                    const cardElement = chartElement.querySelector('.chart-container.card');
                    const elementToHighlight = cardElement || chartElement;
                    elementToHighlight.style.transition = 'box-shadow 0.3s ease';
                    elementToHighlight.style.boxShadow = '0 0 20px rgba(0, 123, 255, 0.5)';
                    setTimeout(() => {
                        elementToHighlight.style.boxShadow = '';
                    }, 2000);

                    chartElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                };

                const sameSection = isSameSection === true || isSameSection === 'true';
                if (sameSection) {
                    scrollAndHighlight();
                    return;
                }

                if (this.standalone) {
                    return;
                }

                const targetSectionIndex = (this.local_page.sections || []).findIndex((section) => section.id == sectionId);
                if (targetSectionIndex === -1) {
                    return;
                }

                this.setActiveTab(targetSectionIndex);
                this.$nextTick(() => {
                    setTimeout(() => scrollAndHighlight(), 100);
                });
            },
            fetchChartSummaryForResize(chart, section) {
                if (!this.shouldShowSummary || !chart?.id || !section?.id || !this.local_page) {
                    return;
                }

                const chartData = chart?.chart;
                if (!chartData || !chartData.data || Object.keys(chartData.data).length === 0) {
                    return;
                }

                const existingSummary = this.chart_summaries[chart.id];
                if (existingSummary?.status === 'queued') {
                    return;
                }
                if (existingSummary?.summary) {
                    return;
                }

                this.chart_summaries = {
                    ...this.chart_summaries,
                    [chart.id]: {
                        ...existingSummary,
                        status: 'queued'
                    }
                };

                const formData = { page: this.getMinimalPageForTakeaways() };
                window.axios.post('/api/insights/section/' + section.id + '/chart/' + chart.id + '/takeaways', formData)
                    .then((response) => {
                        const status = response?.data?.status;
                        const summary = response?.data?.summary ?? null;
                        const relatedCharts = response?.data?.related_charts ?? [];

                        if (status === 'queued') {
                            this.chart_summaries = {
                                ...this.chart_summaries,
                                [chart.id]: {
                                    summary: null,
                                    status: 'queued',
                                    related_charts: []
                                }
                            };
                            return;
                        }

                        this.chart_summaries = {
                            ...this.chart_summaries,
                            [chart.id]: {
                                summary,
                                status: status || 'completed',
                                related_charts: relatedCharts
                            }
                        };
                    })
                    .catch(() => {
                        this.chart_summaries = {
                            ...this.chart_summaries,
                            [chart.id]: {
                                summary: null,
                                status: 'failed',
                                related_charts: []
                            }
                        };
                    });
            },
            getMinimalPageForTakeaways() {
                if (!this.local_page) {
                    return {};
                }

                const page = JSON.parse(JSON.stringify(this.local_page));
                delete page.client;
                if (page.sections && Array.isArray(page.sections)) {
                    page.sections.forEach((section) => {
                        if (section.charts && Array.isArray(section.charts)) {
                            section.charts.forEach((chart) => {
                                if (chart && chart.chart) {
                                    delete chart.chart.data;
                                    delete chart.chart.labels;
                                    delete chart.chart.metric_formats;
                                }
                            });
                        }
                    });
                }

                return page;
            },
            setupChartSummaryListener() {
                // Demo account uses client.id === 0; 0 is falsy so we must check explicitly
                const clientId = this.client?.id;
                if ((clientId !== 0 && !clientId) || typeof window.Echo === 'undefined') {
                    return;
                }

                const self = this;
                this.echoChannel = window.Echo.private('client.' + clientId)
                    .listen('ChartSummaryUpdated', (e) => {
                        try {
                            const chartId = e?.chart_id;
                            if (chartId == null) {
                                return;
                            }

                            // Only handle events for charts this component explicitly queued.
                            if (!Object.prototype.hasOwnProperty.call(self.chart_summaries, chartId)) {
                                return;
                            }

                            if (e.status === 'completed') {
                                self.chart_summaries = {
                                    ...self.chart_summaries,
                                    [chartId]: {
                                        summary: e.summary ?? null,
                                        status: 'completed',
                                        related_charts: e.related_charts ?? []
                                    }
                                };
                            } else if (e.status === 'failed' || e.status === 'timeout') {
                                self.chart_summaries = {
                                    ...self.chart_summaries,
                                    [chartId]: {
                                        summary: null,
                                        status: 'failed',
                                        related_charts: []
                                    }
                                };
                            }
                        } catch (err) {
                            console.warn('ChartSummaryUpdated listener error:', err);
                        }
                    });
            },
            toggleDataLabels(chartId, data, section) {
                const currentValue = data.currentValue;
                const charts = section.charts;
                if (charts) {
                    const chartIndex = charts.findIndex(c => c.id === chartId);
                    if (chartIndex >= 0) {
                        charts[chartIndex].show_data_labels = !currentValue;
                    }
                }
                
                if (data.event && data.event.currentTarget) {
                    const button = data.event.currentTarget;
                    const tooltip = bootstrap.Tooltip.getInstance(button);
                    if (tooltip) {
                        tooltip.hide();
                    }
                    button.blur();
                }
                
                this.$forceUpdate();
            },
            downloadChart(chart_div, section) {
                Context.prototype.getContext = function (contextId) {
                    if (contextId=="2d" || contextId=="2D")
                        return this;
                    return null;
                }
                Context.prototype.style = function () { return this.__canvas.style }
                Context.prototype.getAttribute = function (name) { return this[name];}
                Context.prototype.addEventListener =  function(type, listener, eventListenerOptions) {}

                this.loading = true;
                window.axios.get('/api/export/sizes/' + this.client.id)
                    .then(response => {
                        var sizes = response.data.sizes;
                        
                        let container = document.getElementById(chart_div.id);
                        if (!container) {
                            console.error('Chart container not found:', chart_div.id);
                            this.loading = false;
                            return;
                        }
                        
                        let originalChart = container.querySelector('canvas');
                        if (!originalChart) {
                            console.error('Chart canvas not found in container:', chart_div.id);
                            this.loading = false;
                            return;
                        }

                        const chartjs = Chart.getChart(originalChart);
                        if (!chartjs) {
                            console.error('Chart.js instance not found for export');
                            this.loading = false;
                            return;
                        }

                        const tempContainer = document.createElement('div');
                        tempContainer.style.position = 'absolute';
                        tempContainer.style.left = '-9999px';
                        tempContainer.style.top = '-9999px';
                        document.body.appendChild(tempContainer);

                        const tempCanvas = document.createElement('canvas');
                        tempContainer.appendChild(tempCanvas);
                        
                        const defaultSizes = {
                            FULL_WIDTH: { width: 1200, height: 600 },
                            SINGLE_SMALL: { width: 400, height: 300 }
                        };

                        let size = container.dataset.size;
                        let exportWidth, exportHeight;
                        
                        if (size == 'fullsize' || size == 'large') {
                            exportWidth = sizes?.FULL_WIDTH?.width || defaultSizes.FULL_WIDTH.width;
                            exportHeight = sizes?.FULL_WIDTH?.height || defaultSizes.FULL_WIDTH.height;
                        }
                        else if (size == 'small') {
                            exportWidth = sizes?.SINGLE_SMALL?.width || defaultSizes.SINGLE_SMALL.width;
                            exportHeight = sizes?.SINGLE_SMALL?.height || defaultSizes.SINGLE_SMALL.height;
                        }
                        else {
                            exportWidth = sizes?.FULL_WIDTH?.width || defaultSizes.FULL_WIDTH.width;
                            exportHeight = sizes?.FULL_WIDTH?.height || defaultSizes.FULL_WIDTH.height;
                        }

                        tempCanvas.width = exportWidth;
                        tempCanvas.height = exportHeight;
                        tempCanvas.dataset.width = exportWidth;
                        tempCanvas.dataset.height = exportHeight;
                        
                        const chartType = chartjs.config.type;
                        const chartUsesScales = ['bar', 'line', 'scatter', 'bubble'].includes(chartType);
                        
                        let scalesConfig = {};
                        if (chartUsesScales && chartjs.config.options.scales) {
                            scalesConfig = {
                                ...chartjs.config.options.scales,
                            };
                            
                            if (chartjs.config.options.scales.x) {
                                scalesConfig.x = {
                                    ...chartjs.config.options.scales.x,
                                    ticks: {
                                        ...(chartjs.config.options.scales.x.ticks || {}),
                                        ...(chartjs.config.options.scales.x.ticks?.font ? { font: chartjs.config.options.scales.x.ticks.font } : {})
                                    }
                                };
                            }
                            
                            if (chartjs.config.options.scales.y) {
                                scalesConfig.y = {
                                    ...chartjs.config.options.scales.y,
                                    ticks: {
                                        ...(chartjs.config.options.scales.y.ticks || {}),
                                        ...(chartjs.config.options.scales.y.ticks?.font ? { font: chartjs.config.options.scales.y.ticks.font } : {}),
                                        ...(chartjs.config.options.scales.y.ticks?.callback ? { callback: chartjs.config.options.scales.y.ticks.callback } : {})
                                    }
                                };
                            }
                        }
                        
                        const filteredData = this.getVisibleChartData(chartjs);

                        const exportConfig = {
                            type: chartType,
                            data: filteredData,
                            options: {
                                ...JSON.parse(JSON.stringify(chartjs.config.options)),
                                responsive: false,
                                maintainAspectRatio: false,
                                animation: false,
                                layout: {
                                    padding: chartjs.config.options.layout?.padding || 0
                                },
                                ...(Object.keys(scalesConfig).length > 0 && { scales: scalesConfig }),
                                plugins: {
                                    ...chartjs.config.options.plugins,
                                    legend: {
                                        ...chartjs.config.options.plugins.legend,
                                        labels: {
                                            ...chartjs.config.options.plugins.legend.labels,
                                            ...(chartjs.config.options.plugins.legend.labels?.font ? { font: chartjs.config.options.plugins.legend.labels.font } : {})
                                        }
                                    }
                                }
                            }
                        };

                        this.wrapDatalabelFormatter(exportConfig, chartjs, filteredData);

                        const tempChart = new Chart(tempCanvas, {
                            ...exportConfig,
                            plugins: [ChartDataLabels]
                        });

                        tempChart.update('none');
                        tempChart.update('none');
                        
                        setTimeout(() => {
                            let svg = this.convertToSVG(tempCanvas);
                            svg = this.updateTextElements(svg);
                            if(container.querySelector('.line-chart-template') || container.querySelector('.bar-line-chart-template')){
                                svg = this.addLinesToChart(svg);
                            }

                            let chart_data = filteredData;

                            let data = {
                                client: this.client,
                                page: this.local_page,
                                params: (document.location.search!='') ? document.location.search.substring(1) : '',
                                svg: {
                                    id: chart_div.id,
                                    title: chart_div.chart.title, 
                                    subtitle: chart_div.chart.subtitle, 
                                    svg: svg,
                                    data: chart_data,
                                    size: size
                                }
                            };

                            window.axios.post('/api/export/chart', data, { responseType: 'blob' })
                                .then(response => {
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;

                                    let name = chart_div.chart.title + ' - Chart #' + chart_div.id + ' - ' + this.getDate() + '.pptx';

                                    link.setAttribute('download', name);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    this.loading = false;

                                    tempChart.destroy();
                                    tempContainer.remove();
                                })
                                .catch(error => {
                                    console.error(error);
                                    tempChart.destroy();
                                    tempContainer.remove();
                                });
                        }, 100);
                    })
                    .catch(error => {
                        console.error('Error in export routine:', error);
                        this.loading = false;
                        Swal.fire({
                            icon: 'error',
                            title: 'Export Failed',
                            text: 'There was an error preparing the chart for export. Please try again.'
                        });
                    });
            },
            toggleFilterPanel(){
                this.is_filter_panel_expanded = !this.is_filter_panel_expanded
            },
            dateRangeDisplay(value) {
                if (!value) return '';
                if (value.start_date && value.end_date) {
                    return this.formatUtcDate(value.start_date) + ' - ' + this.formatUtcDate(value.end_date);
                } else if (value.start_date) {
                    return 'On or After ' + this.formatUtcDate(value.start_date);
                } else if (value.end_date) {
                    return 'Prior to ' + this.formatUtcDate(value.end_date);
                }
                return '';
            },
            monthRangeDisplay(value) {
                if (!value) return '';
                if (value.start_date && value.end_date) {
                    return this.formatUtcMonth(value.start_date) + ' - ' + this.formatUtcMonth(value.end_date);
                } else if (value.start_date) {
                    return 'On or After ' + this.formatUtcMonth(value.start_date);
                } else if (value.end_date) {
                    return 'Prior to ' + this.formatUtcMonth(value.end_date);
                }
                return '';
            },
            formatUtcDate(dateString) {
                if (!dateString) return '';
                const date = new Date(dateString);
                const offset = date.getTimezoneOffset();
                date.setMinutes(date.getMinutes() + offset);
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                const yyyy = date.getFullYear();
                return `${mm}/${dd}/${yyyy}`;
            },
            formatUtcMonth(dateString) {
                if (!dateString) return '';
                const date = new Date(dateString);
                const offset = date.getTimezoneOffset();
                date.setMinutes(date.getMinutes() + offset);
                const month = date.toLocaleString('default', { month: 'short' });
                const year = date.getFullYear();
                return `${month} ${year}`;
            },
            setDefaultFilterValues() {
                if (this.local_page.dimensions) {
                    for (var i = 0; i < this.local_page.dimensions.length; i++) {
                        let dim = this.local_page.dimensions[i];
                        if(dim.field_type == "multi_select" || dim.field_type == "array_single_select" || dim.field_type == "array_multi_select") {
                            this.local_page.dimensions[i].value = [];
                        } else if(dim.field_type == "single_select") {
                            if (dim.column === 'period_type') {
                                this.local_page.dimensions[i].value = 'Trailing 12 Months';
                            } else {
                                this.local_page.dimensions[i].value = "";
                            }
                        }
                    }
                }
            },
            loadSectionTab() {
                for(var i = 0; i < this.local_page.sections.length; i++) {
                    if(i == 1) {
                        this.local_page.sections[i].is_section_tab_active = true;
                    } else {
                        this.local_page.sections[i].is_section_tab_active = false;
                    }
                }
            },
            initializeTooltips() {
                this.tooltips.forEach(tooltip => {
                    try {
                        tooltip.dispose();
                    } catch (e) {
                        // ignore if already disposed
                    }
                });
                this.tooltips = [];

                if (!this.benchmarkPage) return;
                const tooltipElements = this.benchmarkPage.querySelectorAll('[data-bs-toggle="tooltip"]');
                tooltipElements.forEach(element => {
                    if (!element || !document.contains(element)) return;
                    try {
                        const tooltip = new Tooltip(element);
                        this.tooltips.push(tooltip);
                    } catch (e) {
                        // Bootstrap Tooltip can throw if element is detached or invalid
                    }
                });
            },
            setActiveTab(index) {
                if(index == 0) return;

                for(var i = 0; i < this.local_page.sections.length; i++) {
                    if(i == index) {
                        this.local_page.sections[i].is_section_tab_active = true;
                    } else {
                        this.local_page.sections[i].is_section_tab_active = false;
                    }
                }
                this.active_tab_index = index;

                nextTick(() => {
                    this.initializeTooltips();
                });
            },
            setActiveViewTab(tab) {
                this.active_view_tab = tab;
            },
            getNextSectionTitle(sectionIndex) {
                const next = this.nextSectionInOrder;
                return next ? next.title : '';
            },
            getPreviousSectionTitle(sectionIndex) {
                const prev = this.previousSectionInOrder;
                return prev ? prev.title : '';
            },
            updatePage(updatedPages) {
                try {
                    let benchmarkPageData, wilandPageData;
                    
                    if (updatedPages.benchmark_page && updatedPages.wiland_page) {
                        benchmarkPageData = updatedPages.benchmark_page;
                        wilandPageData = updatedPages.wiland_page;
                        
                        this.benchmark_page = benchmarkPageData;
                        this.wiland_page = wilandPageData;
                    } else {
                        benchmarkPageData = updatedPages;
                        wilandPageData = this.wiland_page;
                    }
                    
                    const combinedSections = [];
                    
                    combinedSections.push({
                        id: 0,
                        title: '',
                        is_section_tab_active: false
                    });
                    
                    if (wilandPageData && wilandPageData.sections && wilandPageData.sections.length > 0) {
                        combinedSections.push({
                            ...wilandPageData.sections[0],
                            is_section_tab_active: false
                        });
                    }
                    
                    if (benchmarkPageData && benchmarkPageData.sections && benchmarkPageData.sections.length > 0) {
                        combinedSections.push({
                            ...benchmarkPageData.sections[0],
                            is_section_tab_active: false
                        });
                    }
                    
                    this.local_page = {
                        ...benchmarkPageData,
                        sections: combinedSections
                    };
                    
                    if (updatedPages.dimensions && Array.isArray(updatedPages.dimensions)) {
                        this.local_page.dimensions = updatedPages.dimensions;
                    } else if (benchmarkPageData.dimensions) {
                        this.local_page.dimensions = benchmarkPageData.dimensions;
                    }
                    
                    this.page = {
                        ...this.page,
                        sections: combinedSections
                    };
                    
                    this.initializeLocalCharts();
                    this.setActiveTab(this.active_tab_index);

                    Swal.close();

                    this.showNotification({
                        text: 'The benchmark data has been refreshed!'
                    });
                } catch (error) {
                    console.error('Error updating page:', error);
                    Swal.fire({
                        title: 'Error',
                        text: 'Something went wrong while updating the page. Please try again.',
                        icon: 'error',
                        confirmButtonText: "OK",
                        customClass: {
                            popup: 'swal-error'
                        },
                    });
                }
            },
            goToNextSection() {
                const next = this.nextSectionInOrder;
                if (!next) return;
                const idx = (this.local_page.sections || []).findIndex(s => s.id === next.id);
                if (idx >= 0) {
                    this.setActiveTab(idx);
                    document.getElementById('top-of-page').scrollIntoView({behavior: 'smooth'});
                }
            },
            goToPreviousSection() {
                const prev = this.previousSectionInOrder;
                if (!prev) return;
                const idx = (this.local_page.sections || []).findIndex(s => s.id === prev.id);
                if (idx >= 0) {
                    this.setActiveTab(idx);
                    document.getElementById('top-of-page').scrollIntoView({behavior: 'smooth'});
                }
            },
            navigateToFilter(filter) {
                this.$emit('navigateToFilter', filter);
            },
            removeAppliedFilter(filter) {
                this.$emit('removeAppliedFilter', filter);
            },
            openSupportModal() {
                this.selected_ticket_type = 'FEATURE_REQUEST';
            },
            handleSidebarStorageChange(e) {
                if (e.key === 'sidebar_collapsed') {
                    this.sidebar_collapsed = e.newValue === 'true';
                }
            },
            handleSidebarToggleEvent() {
                this.sidebar_collapsed = localStorage.getItem('sidebar_collapsed') === 'true';
            },
        },
        beforeUnmount() {
            this.tooltips.forEach(tooltip => {
                tooltip.dispose();
            });

            if (this.echoChannel) {
                try {
                    this.echoChannel.stopListening('ChartSummaryUpdated');
                } catch (e) {
                    // no-op
                }
                this.echoChannel = null;
            }
            
            if (this.onResize) {
                window.removeEventListener('resize', this.onResize);
            }
            window.removeEventListener('storage', this.handleSidebarStorageChange);
            window.removeEventListener('sidebar-toggle', this.handleSidebarToggleEvent);
        }
    });
</script>
